import React, { useState } from 'react';
import { X, Share2, Copy, Check, BookOpen, Book } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { useAuthenticatedFetch } from '../utils/api';

const ShareModal = ({ isOpen, onClose, classId, lectureId, className, lectureName }) => {
  const { theme } = useTheme();
  const [shareLink, setShareLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [shareType, setShareType] = useState(null);
  const authenticatedFetch = useAuthenticatedFetch();

  const generateShareLink = async () => {
    if (!shareType) {
      toast.error('Please select what you want to share');
      return;
    }

    setIsGenerating(true);
    try {
      const endpoint = shareType === 'class' 
        ? `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${classId}/share-link`
        : `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${lectureId}/share-link`;

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const { shareLink } = await response.json();
        setShareLink(shareLink);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to generate share link');
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setIsCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-lg p-6 w-full max-w-md" style={{ background: theme.background }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
            Share Content
          </h2>
          <button 
            onClick={onClose} 
            style={{ color: theme.accent }}
            className="hover:opacity-80"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm mb-4" style={{ color: theme.text }}>
              What would you like to share?
            </p>
            <div className="grid grid-cols-2 gap-4">
              {lectureId && (
                <button
                  onClick={() => setShareType('lecture')}
                  className="p-4 rounded-lg flex flex-col items-center gap-2 transition-all"
                  style={{ 
                    background: shareType === 'lecture' ? theme.primary : theme.secondary,
                    border: `2px solid ${shareType === 'lecture' ? theme.primary : theme.border}`,
                    color: theme.text
                  }}
                >
                  <Book size={24} />
                  <span className="text-sm font-medium">Just This Lecture</span>
                  <span className="text-xs text-center" style={{ color: theme.accent }}>
                    Share only "{lectureName}"
                  </span>
                </button>
              )}
              <button
                onClick={() => setShareType('class')}
                className="p-4 rounded-lg flex flex-col items-center gap-2 transition-all"
                style={{ 
                  background: shareType === 'class' ? theme.primary : theme.secondary,
                  border: `2px solid ${shareType === 'class' ? theme.primary : theme.border}`,
                  color: theme.text
                }}
              >
                <BookOpen size={24} />
                <span className="text-sm font-medium">Entire Class</span>
                <span className="text-xs text-center" style={{ color: theme.accent }}>
                  Share all lectures in "{className}"
                </span>
              </button>
            </div>
          </div>

          {!shareLink ? (
            <button
              onClick={generateShareLink}
              disabled={isGenerating || !shareType}
              className="w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
              style={{ 
                background: theme.primary,
                color: theme.text,
                opacity: (isGenerating || !shareType) ? 0.5 : 1,
                cursor: (isGenerating || !shareType) ? 'not-allowed' : 'pointer'
              }}
            >
              <Share2 size={18} />
              {isGenerating ? 'Generating...' : 'Generate Share Link'}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="w-full px-3 py-2 rounded-lg focus:outline-none"
                  style={{ 
                    background: theme.secondary,
                    color: theme.text,
                    border: `1px solid ${theme.border}`
                  }}
                />
                <button
                  onClick={copyToClipboard}
                  className="p-2 rounded-lg transition-colors"
                  style={{ 
                    background: theme.primary,
                    color: theme.text
                  }}
                  title={isCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {isCopied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
              <p className="text-xs" style={{ color: theme.accent }}>
                {shareType === 'lecture' 
                  ? 'Anyone with a Classify account can use this link to view and add this lecture to their account.'
                  : 'Anyone with a Classify account can use this link to view and add this class and all its lectures to their account.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal; 